import { useEffect } from "react";
import { createPortal } from "react-dom";
function getActivityMinimumAge(activity) {
    const raw =
        activity?.minimum_age ??
        activity?.min_age ??
        activity?.age_minimum ??
        activity?.minimum_required_age ??
        activity?.min_required_age ??
        null;

    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.max(0, Math.floor(n));
}

function getAgeRestrictionCopy(minAge, isPassPerk = false) {
    if (!minAge) return "";

    const label = isPassPerk ? "perk" : "activity";
    return `This ${label} is listed for ages ${minAge} and up. Final vendor safety rules may still apply at check-in.`;
}


function getActivityDisplaySection(activity) {
    return String(activity?.display_section || "").trim().toLowerCase();
}

function isPassPerkActivity(activity) {
    const section = getActivityDisplaySection(activity);
    return section === "pass_perks" || activity?.is_pass_perk === true;
}

function formatDuration(value) {
    const duration = value ?? 1;
    const numeric = Number(duration);

    if (!Number.isFinite(numeric)) return `${duration} hrs`;
    if (numeric === 1) return "1 hr";

    return `${numeric} hrs`;
}

// Only activities that match one of these checks will show the upgrade CTA.
// Best option: add upgrade_available: true, is_upgradeable: true, or is_upgrade_activity: true
// to the two activity records coming from your API/Supabase.
const UPGRADE_MODAL_ACTIVITY_IDS = new Set([
    // "replace-with-activity-id-1",
    // "replace-with-activity-id-2",
]);

const UPGRADE_MODAL_ACTIVITY_NAMES = new Set([
    // "replace with exact activity name 1",
    // "replace with exact activity name 2",
]);

function normalizeActivityKey(value) {
    return String(value ?? "").trim().toLowerCase();
}

function isUpgradeModalActivity(activity) {
    const activityId = normalizeActivityKey(activity?.id);
    const activityName = normalizeActivityKey(activity?.name);

    return (
        activity?.upgrade_available === true ||
        activity?.is_upgradeable === true ||
        activity?.is_upgrade_activity === true ||
        UPGRADE_MODAL_ACTIVITY_IDS.has(activityId) ||
        UPGRADE_MODAL_ACTIVITY_NAMES.has(activityName)
    );
}

export default function ActivityDetailModal({ activity, onClose }) {
    useEffect(() => {
        if (!activity || typeof document === "undefined") return undefined;

        document.body.classList.add("bbp-modal-open");

        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.classList.remove("bbp-modal-open");
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [activity, onClose]);

    if (!activity) return null;

    const duration = activity.duration_hours ?? activity.duration ?? 1;
    const minimumAge = getActivityMinimumAge(activity);
    const hasAdult = typeof activity.adult_price === "number" && activity.adult_price > 0;
    const hasChild = typeof activity.child_price === "number" && activity.child_price > 0;

    const isPassPerk = isPassPerkActivity(activity);
    const ageRestrictionCopy = getAgeRestrictionCopy(minimumAge, isPassPerk);
    const requiresReservation = !!activity.requires_reservation;
    const showUpgradeCta = requiresReservation && isUpgradeModalActivity(activity);
    const bookingUrl = activity.booking_url || activity.website || "";

    const discountCode = activity.bbp_discount_code || "";
    const perkLabel = activity.perk_label || "";
    const bookingInstructions = activity.booking_instructions || "";
    const redemptionInstructions = activity.redemption_instructions || "";

    const imageSrc = activity.image_url || activity.image || "/assets/Graphics/Hero_New.png?v=2";

    const statusClass = isPassPerk
        ? "is-perk"
        : requiresReservation
            ? "is-reservation"
            : "is-scan";

    const statusText = isPassPerk
        ? "Pass Perk"
        : requiresReservation
            ? "Reservation Required"
            : "Scan & Go";

    const modalMarkup = (
        <div className="modal-overlay activity-modal-overlay is-open" onClick={onClose}>
            <div
                className="activity-modal activity-modal-v2"
                role="dialog"
                aria-modal="true"
                aria-label={activity.name || "Activity details"}
                onClick={(e) => e.stopPropagation()}
            >
                <button className="modal-close" onClick={onClose} aria-label="Close modal" type="button">
                    <svg
                        className="modal-close-icon"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </button>

                <div className="activity-modal-media">
                    <img
                        src={imageSrc}
                        alt={activity.name || "Beach Bum Pass activity"}
                        className="modal-image"
                    />

                    <div className={`activity-status-pill ${statusClass}`}>
                        {statusText}
                    </div>


                </div>

                <div className="activity-modal-header">
                    <div className="activity-modal-kicker">
                        {activity.category || (isPassPerk ? "Pass Perk" : "Beach Bum Experience")}
                    </div>

                    <h2>{activity.name}</h2>

                    {activity.location ? (
                        <p className="modal-location">{activity.location}</p>
                    ) : null}
                </div>

                {perkLabel ? (
                    <div className="activity-modal-booking">
                        <div className="activity-modal-booking-title">{perkLabel}</div>
                        <div className="activity-modal-booking-copy">
                            {activity.description || "A little extra Beach Bum goodness tucked into your pass."}
                        </div>
                    </div>
                ) : activity.description ? (
                    <p className="modal-description">{activity.description}</p>
                ) : null}



                {requiresReservation ? (
                    <div className="activity-modal-booking">


                        {discountCode ? (
                            <div className="activity-modal-code-wrap">
                                <div className="activity-modal-code-label">Beach Bum Pass Code</div>
                                <div className="activity-modal-code-block">{discountCode}</div>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {isPassPerk && redemptionInstructions ? (
                    <div className="activity-modal-booking">
                        <div className="activity-modal-booking-title">How to Use This Perk</div>
                        <div className="activity-modal-booking-copy">
                            {redemptionInstructions}
                        </div>

                        {discountCode ? (
                            <div className="activity-modal-code-wrap">
                                <div className="activity-modal-code-label">Beach Bum Pass Code</div>
                                <div className="activity-modal-code-block">{discountCode}</div>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {(!isPassPerk || minimumAge > 0) ? (
                    <div className="modal-info activity-modal-meta">
                        {!isPassPerk ? (
                            <p>
                                <strong>Duration</strong>
                                <span>{formatDuration(duration)}</span>
                            </p>
                        ) : null}

                        {minimumAge > 0 ? (
                            <p>
                                <strong>Minimum Age</strong>
                                <span>{minimumAge}+</span>
                            </p>
                        ) : null}

                        {!isPassPerk && hasAdult ? (
                            <p>
                                <strong>Adult Price</strong>
                                <span>${activity.adult_price}</span>
                            </p>
                        ) : null}

                        {!isPassPerk && hasChild ? (
                            <p>
                                <strong>Child Price</strong>
                                <span>${activity.child_price}</span>
                            </p>
                        ) : null}
                    </div>
                ) : null}

                {bookingUrl ? (
                    <div className="activity-modal-actions">
                        <a
                            className="btn-primary activity-modal-link"
                            href={bookingUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {showUpgradeCta
                                ? "CLICK HERE TO RESERVE AND UPGRADE YOUR EXPERIENCE!"
                                : requiresReservation
                                    ? "Reserve Your Experience"
                                    : "Visit Website"}
                        </a>

                        {requiresReservation ? (
                            <p className="activity-modal-note">
                                Reserve directly with the vendor. Your Beach Bum Pass is scanned when you arrive.
                            </p>
                        ) : isPassPerk ? (
                            <p className="activity-modal-note">
                                Check the vendor details above, then show your Beach Bum Pass when you redeem this perk.
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );

    if (typeof document === "undefined") return modalMarkup;

    return createPortal(modalMarkup, document.body);
}
